'use client';
import {
  Box,
  Container,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  GridItem,
  Heading,
  Input,
  InputGroup,
  InputLeftAddon,
  InputRightAddon,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Slider,
  SliderFilledTrack,
  SliderMark,
  SliderThumb,
  SliderTrack,
  Stack,
  Text,
} from '@chakra-ui/react';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import ButtonCustom from '@/app/_components/button/ButtonCustom';
import { PriceFormat } from '@/app/_components/price-format/price-format';
import {
  DEFAULT_BALLOON,
  DEFAULT_BALLOON_RANGE,
  DEFAULT_INTEREST,
  DEFAULT_PRICE_VEHICLE,
  MAX_INTEREST,
  MIN_INTEREST,
  SLIDER_MODE1,
  SLIDER_STEP_MODE_5000,
} from '@/constants/payment-method/payment.constants';
import { useFinance } from '@/hooks/finance/finance.hook';
import { useFloatingCTA } from '@/hooks/floating-cta.hook';
import {
  DefaultRepaymentValue,
  getInstallementValue,
} from '@/services/new-cars/new-cars.service';
import { BalloonTypes } from '@/types/filters/filters.types';

import BallonPaymentButtons from './BallonPaymentButtons';
import Styles from './FinanceCalculator.module.scss';
import MonthlyPaymentTermButtons from './MonthlyPaymentTermButtons';

interface FinanceCalculatorProps {
  defaultValues: DefaultRepaymentValue;
}

const FinanceCalculator: React.FC<FinanceCalculatorProps> = ({
  defaultValues,
}) => {
  const { setSelectedFinance } = useFinance();

  const defaultRepaymentValues = defaultValues?.find(
    (a) => a.year_offset === 0,
  );
  const initialFinanceFilter = {
    deposit: defaultRepaymentValues?.deposit ?? 0,
    balloon: defaultRepaymentValues?.baloon_payment ?? DEFAULT_BALLOON,
    interest: defaultRepaymentValues?.interest_rate ?? DEFAULT_INTEREST,
    paymentTerm: defaultRepaymentValues?.term ?? 60,
    price: Number(DEFAULT_PRICE_VEHICLE),
  };
  const defaultCalulation = {
    monthlyPayment: 0,
    totalRepayment: 0,
    fees: 0,
    registration_fee: 0,
    deposit: 0,
    loanAmount: 0,
    totalInterest: 0,
    estimatedPayoffDate: '',
    effectiveAnnualRate: '',
  };

  const [financeFilter, setFinanceFilter] = useState(initialFinanceFilter);
  const [financeCalculation, setFinanceCalculation] =
    useState(defaultCalulation);
  const [activeBalloonOption, setActiveBalloonOption] = useState<'%' | 'R'>(
    '%',
  );
  const { openLeadForm } = useFloatingCTA();

  const isPercentageMode = activeBalloonOption === '%';

  const getBallonOption = useCallback(() => {
    return {
      prefix: activeBalloonOption === '%' ? '%' : 'R',
      max: isPercentageMode ? DEFAULT_BALLOON_RANGE.max : DEFAULT_PRICE_VEHICLE,
      sliderStep: isPercentageMode ? SLIDER_MODE1 : SLIDER_STEP_MODE_5000,
    };
  }, [isPercentageMode]);

  const priceInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    calculateEstimatedPayment();
  }, [
    activeBalloonOption,
    financeFilter.price,
    financeFilter.deposit,
    financeFilter.balloon,
    financeFilter.paymentTerm,
    financeFilter.interest,
  ]);

  const calculateEstimatedPayment = async () => {
    if (!defaultRepaymentValues) return;

    const price = Number(financeFilter?.price) || 0;
    const interest = Number(financeFilter?.interest) || DEFAULT_INTEREST;
    const paymentTerm = Number(financeFilter?.paymentTerm) || 60;
    const balloon = Number(financeFilter?.balloon) || 0;
    const deposit = Number(financeFilter?.deposit) || 0;

    if (price <= 0 || paymentTerm <= 0 || interest < 0) {
      setFinanceCalculation(defaultCalulation);
      return;
    }

    try {
      const backendVal = await getInstallementValue({
        financeFilter: {
          price,
          interest,
          paymentTerm,
          balloon,
          deposit,
        },
        balloonOption: isPercentageMode
          ? BalloonTypes.percentage
          : BalloonTypes.value,
        defaultValues: defaultRepaymentValues,
      });

      if (backendVal) {
        setFinanceCalculation({
          monthlyPayment: backendVal.monthlyPayment ?? 0,
          totalRepayment: backendVal.totalRepayment ?? 0,
          fees: backendVal.fees ?? 0,
          registration_fee: backendVal.registration_fee ?? 0,
          deposit: deposit,
          loanAmount: backendVal.loanAmount ?? 0,
          totalInterest: backendVal.totalInterest ?? 0,
          estimatedPayoffDate: backendVal.estimatedPayoffDate ?? '',
          effectiveAnnualRate: backendVal.effectiveAnnualRate ?? '',
        });
      } else {
        setFinanceCalculation(defaultCalulation);
      }
    } catch (error) {
      console.error('Error calculating payment:', error);
      setFinanceCalculation(defaultCalulation);
    }
  };
  const handleDepositChange = (value: string) => {
    const numericValue = Number(value.replace(/[^0-9.-]+/g, ''));
    
    if (isNaN(numericValue) || !isFinite(numericValue)) {
      return;
    }

    const maxPrice = Number(financeFilter.price) || 0;
    
    if (maxPrice > 0 && numericValue <= maxPrice * 0.7 && numericValue >= 0) {
      setFinanceFilter((prev) => ({ ...prev, deposit: numericValue }));
    }
  };

  const handleBalloonChange = (value: number) => {
    if (isNaN(value) || !isFinite(value)) {
      return;
    }

    const limits = isPercentageMode
      ? DEFAULT_BALLOON_RANGE
      : { min: 0, max: DEFAULT_PRICE_VEHICLE };

    if (value > limits.max || value < limits.min) {
      return;
    }
    setFinanceFilter((prev) => ({ ...prev, balloon: value }));
  };

  const handleInterestChange = (value: number) => {
    if (isNaN(value) || !isFinite(value)) {
      return;
    }
    
    if (value > MAX_INTEREST || value < MIN_INTEREST) {
      return;
    }
    setFinanceFilter((prev) => ({ ...prev, interest: value }));
  };

  const handlePaymentTermChange = (value: string) => {
    const numericValue = Number(value);
    
    if (isNaN(numericValue) || !isFinite(numericValue) || numericValue <= 0) {
      return;
    }
    
    setFinanceFilter((prev) => ({ ...prev, paymentTerm: numericValue }));
  };

  const labelStyles = {
    mt: '3',
    fontSize: '12px',
    color: 'gray.600',
  };

  const handlePriceChange = (val: string, valNumber: number) => {
    if (isNaN(valNumber) || !isFinite(valNumber) || valNumber < 0) {
      return;
    }
    
    setFinanceFilter((prev) => ({ ...prev, price: valNumber }));
  };

  const navigateToFinance = () => {
    setSelectedFinance({
      dealerRef: '',
      dealerFloor: 'NEWKIA',
      vehicleImage: '',
      mmCode: '',
      price: Number(financeFilter?.price),
      used: false,
      year: new Date().getFullYear(),
      make: undefined,
      model: undefined,
      variant: undefined,
      mileage: '0',
    } as any);

    openLeadForm('finance');
  };

  return (
    <Container
      maxW={'1280'}
      width={'100%'}
      className={Styles.financeOptionWrapper}
      paddingY={{ base: 10, lg: 20 }}
      paddingX={{ base: 6, '2xl': 0 }}
    >
      <Box
        className={Styles.headingElement}
        marginBottom={{ base: 10, lg: 12 }}
      >
        <Heading as="h2" className={Styles.titleLg}>
          Explore your Finance options
        </Heading>
      </Box>

      <Grid
        templateColumns={{ base: 'repeat(2, 1fr)', lg: 'repeat(12, 1fr)' }}
        gap={{ base: '40px', xl: '100px' }}
      >
        <GridItem colSpan={{ base: 2, lg: 7 }}>
          <Stack gap={'32px'}>
            <FormControl>
              <Flex justifyContent={'space-between'}>
                <FormLabel className={Styles.formLabel}>
                  Vehicle Price
                </FormLabel>
                <Box mb={3} className={Styles.vehiclePriceResult}>
                  <NumberInput
                    size="md"
                    value={financeFilter.price}
                    step={1000}
                    onChange={handlePriceChange}
                  >
                    <NumberInputField
                      autoFocus
                      value={`R ${financeFilter.price}`}
                      border={0}
                      borderRadius={0}
                      borderColor="transparent"
                      w={130}
                      ref={priceInputRef}
                      disabled={true}
                      _disabled={{ 
                        opacity: 1,
                        cursor: 'default',
                        backgroundColor: 'rgba(0, 0, 0, 0.04)'
                      }}
                    />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                </Box>
              </Flex>

              <Slider
                aria-label="Price"
                onChange={(val) =>
                  setFinanceFilter((prev) => ({ ...prev, price: val }))
                }
                value={financeFilter.price}
                min={0}
                max={1100000}
                step={1000}
                defaultValue={DEFAULT_PRICE_VEHICLE}
              >
                <SliderMark value={0} ml={0} {...labelStyles}>
                  R 0
                </SliderMark>
                <SliderMark
                  value={DEFAULT_PRICE_VEHICLE}
                  ml={'auto'}
                  left={'89% !important'}
                  {...labelStyles}
                >
                  <PriceFormat prefix="R" value={1100000} />
                </SliderMark>
                <SliderMark
                  value={financeFilter.price}
                  textAlign="center"
                  color="black"
                  mt="3"
                  ml="-5"
                  w="18"
                  fontSize="16px"
                  fontWeight="600"
                  bg={'white'}
                >
                  R {financeFilter.price.toLocaleString('en-US')}
                </SliderMark>
                <SliderTrack bg={'gray.300'} h={'2px'}>
                  <SliderFilledTrack bg={'gray.600'} />
                </SliderTrack>
                <SliderThumb
                  border={2}
                  borderStyle={'solid'}
                  borderColor={'gray.900'}
                  boxSize={4}
                  _focusWithin={{
                    borderColor: 'gray.900',
                    boxShadow: '0 0 0 3px rgba(0, 0, 0, 0.2)',
                  }}
                />
              </Slider>
            </FormControl>
            <FormControl>
              <FormLabel className={Styles.formLabel}>Deposit</FormLabel>
              <Input
                value={financeFilter?.deposit}
                onChange={(e) => handleDepositChange(e.target.value)}
                className={Styles.formInput}
                variant="flushed"
                prefix="R"
              />
            </FormControl>
            <FormControl>
              <Flex justifyContent={'space-between'}>
                <FormLabel className={Styles.formLabel}>
                  Balloon Payment
                </FormLabel>
                <BallonPaymentButtons
                  value={activeBalloonOption}
                  onChange={setActiveBalloonOption as any}
                />
              </Flex>
              <InputGroup className={Styles.inputGroup}>
                {getBallonOption().prefix === 'R' && (
                  <InputLeftAddon className={Styles.prefix}>R</InputLeftAddon>
                )}
                <Input
                  prefix="R"
                  value={financeFilter.balloon}
                  onChange={(e) =>
                    handleBalloonChange(
                      Number(e.target.value.replace(/[^0-9.-]+/g, '')),
                    )
                  }
                  className={Styles.formInput}
                  variant="flushed"
                />

                {getBallonOption().prefix === '%' && (
                  <InputRightAddon className={Styles.prefix}>%</InputRightAddon>
                )}
              </InputGroup>
            </FormControl>
            <FormControl>
              <FormLabel className={Styles.formLabel}>Interest Rate</FormLabel>
              <Slider
                aria-label="Interest Rate"
                onChange={handleInterestChange}
                min={MIN_INTEREST}
                max={MAX_INTEREST}
                step={0.5}
                defaultValue={financeFilter.interest}
              >
                <SliderMark value={MIN_INTEREST} ml={0} {...labelStyles}>
                  {MIN_INTEREST}%
                </SliderMark>
                <SliderMark value={MAX_INTEREST} ml={'-5'} {...labelStyles}>
                  {MAX_INTEREST}%
                </SliderMark>
                <SliderMark
                  value={financeFilter.interest}
                  textAlign="center"
                  color="black"
                  mt="3"
                  ml="-5"
                  w="12"
                  fontSize="16px"
                  fontWeight="600"
                  bg={'white'}
                >
                  {financeFilter.interest}%
                </SliderMark>
                <SliderTrack bg={'gray.300'} h={'2px'}>
                  <SliderFilledTrack bg={'gray.600'} />
                </SliderTrack>
                <SliderThumb
                  border={2}
                  borderStyle={'solid'}
                  borderColor={'gray.900'}
                  boxSize={4}
                  _focusWithin={{
                    borderColor: 'gray.900',
                    boxShadow: '0 0 0 3px rgba(0, 0, 0, 0.2)',
                  }}
                />
              </Slider>
            </FormControl>
            <FormControl as="fieldset">
              <FormLabel as="legend" className={Styles.formLabel}>
                Monthly Payment Term
              </FormLabel>
              <MonthlyPaymentTermButtons
                value={financeFilter.paymentTerm.toString()}
                onChange={handlePaymentTermChange}
              />
            </FormControl>
          </Stack>
        </GridItem>
        <GridItem colSpan={{ base: 2, lg: 5 }}>
          <Box className={Styles.headingElement} marginBottom={8}>
            <Text marginBottom={2}>Estimated Monthly Payment</Text>
            <Heading as="h3" className={Styles.titleLg}>
              <PriceFormat
                value={financeCalculation.monthlyPayment}
                prefix="R"
              />
            </Heading>
          </Box>
          <Box className={Styles.priceBreakdownBox}>
            <Box className={Styles.headingElement} marginBottom={'24px'}>
              <Heading as="h3" className={Styles.titleMd}>
                Price Breakdown
              </Heading>
            </Box>
            <Stack gap={4} marginBottom={8}>
              <Flex justifyContent={'space-between'} alignItems={'center'}>
                <Text fontSize={'sm'}>Deposit</Text>
                <Text fontSize={'md'} fontWeight={'600'}>
                  <PriceFormat prefix="R" value={financeFilter.deposit} />
                </Text>
              </Flex>
              <Flex justifyContent={'space-between'} alignItems={'center'}>
                <Text fontSize={'sm'}>Balloon Payment</Text>
                <Text fontSize={'md'} fontWeight={'600'}>
                  {getBallonOption().prefix === '%' ? (
                    financeFilter.balloon + ' %'
                  ) : (
                    <PriceFormat prefix="R" value={financeFilter.balloon} />
                  )}
                </Text>
              </Flex>
              <Flex justifyContent={'space-between'} alignItems={'center'}>
                <Text fontSize={'sm'}>Interest Rate</Text>
                <Text fontSize={'md'} fontWeight={'600'}>
                  {financeFilter.interest} %
                </Text>
              </Flex>
              <Flex justifyContent={'space-between'} alignItems={'center'}>
                <Text fontSize={'sm'}>Payment Term</Text>
                <Text fontSize={'md'} fontWeight={'600'}>
                  {financeFilter.paymentTerm} Months
                </Text>
              </Flex>
              <Flex justifyContent={'space-between'} alignItems={'center'}>
                <Text fontSize={'sm'}>Estimated Amount Financed</Text>
                <Text fontSize={'md'} fontWeight={'600'}>
                  <PriceFormat
                    value={financeCalculation?.totalRepayment}
                    prefix="R"
                  />
                </Text>
              </Flex>
            </Stack>
            <Stack gap={6}>
              <ButtonCustom
                onClick={() => openLeadForm('test-drive')}
                extraClass={Styles.ctaBtn}
              >
                Book A test drive
              </ButtonCustom>
              <ButtonCustom
                className="btnOutline"
                extraClass={Styles.ctaBtn}
                onClick={() => navigateToFinance()}
              >
                Apply for Finance
              </ButtonCustom>
            </Stack>
            <Box marginTop={6}>
              <Popover closeOnBlur={true}>
                <PopoverTrigger>
                  <ButtonCustom className={'btnTextLink'}>
                    Disclaimer
                  </ButtonCustom>
                </PopoverTrigger>
                <Box position={'relative'}>
                  <PopoverContent borderRadius={0}>
                    <PopoverArrow />
                    <PopoverHeader border={0}>
                      <PopoverCloseButton />
                    </PopoverHeader>
                    <PopoverBody>
                      <Text>
                        All calculations made on calculators supplied on this
                        site, together with rates quoted, are guidelines only
                        and are subject to confirmation at the time of
                        finalising any transactions. All information regarding
                        the products, fees and/or costs which are included in
                        and form a fundamental basis of the calculations are
                        subject to change at any time prior to a final
                        pre-agreement quote being handed to the User.
                      </Text>
                      <Text marginTop={'12px'}>
                        {`The User indemnifies KiaRetail.co.za against any loss or
                        liability, which the User may suffer as a result of the
                        use of any calculator. The site and all information
                        provided on this site and the services provided on this
                        site, are provided "as is". The information provided on
                        this site should not be treated as professional advice`}
                        of any kind.
                      </Text>
                    </PopoverBody>
                  </PopoverContent>
                </Box>
              </Popover>
            </Box>
          </Box>
        </GridItem>
      </Grid>
    </Container>
  );
};

export default FinanceCalculator;
